import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Calendar, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Footer } from '../../components/Footer';
import { MultiStudioSelection } from '../../components/booking/MultiStudioSelection';
import { CalendlyCalendar } from '../../components/booking/CalendlyCalendar';
import { MultiStudioCalendar } from '../../components/booking/MultiStudioCalendar';
import { ExtraCameraSection } from '../../components/booking/ExtraCameraSection';
import { ExtraMicrophoneSection } from '../../components/booking/ExtraMicrophoneSection';
import { VideoEditingSection } from '../../components/booking/VideoEditingSection';
import { ReelsEditingSection } from '../../components/booking/ReelsEditingSection';
import { FileDeliverySection } from '../../components/booking/FileDeliverySection';
import { PaymentOptions } from '../../components/booking/PaymentOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatIDR = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Derive hours from pack for studio selection
const getPackHours = (pack) => {
  if (pack.hours) return pack.hours;
  if (!pack.duration) return 2;
  const d = pack.duration.toLowerCase();
  if (d.includes('full day') || d.includes('10')) return 10;
  if (d.includes('half day')) return 4;
  if (d.includes('day')) {
    const match = d.match(/(\d+)/);
    return match ? parseInt(match[1]) * 8 : 8;
  }
  const hourMatch = d.match(/(\d+)\s*h/);
  if (hourMatch) return parseInt(hourMatch[1]);
  const minMatch = d.match(/(\d+)\s*min/);
  if (minMatch) return Math.max(1, Math.ceil(parseInt(minMatch[1]) / 60));
  return 2;
};

export default function ServiceBookingPage({ 
  serviceType,
  serviceTitle,
  serviceSubtitle,
  packs,
  addons = [],
  customFields = [],
  backLink = '/services'
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Pack state
  const [selectedPack, setSelectedPack] = useState(null);

  // Studio state
  const [studioConfig, setStudioConfig] = useState(null);

  // Date/time state
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [multiStudioSlots, setMultiStudioSlots] = useState({ studio1: [], studio2: [] });

  // Options state (mirrors original BookingPage)
  const [options, setOptions] = useState({
    extra_cameras: 0,
    extra_microphones: 0,
    video_editing: { enabled: false, duration: '', cameras: '1_camera', isCustomQuote: false },
    reels: [],
    file_delivery: 'on_site'
  });

  // Service-specific addons
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Custom fields
  const [customFieldValues, setCustomFieldValues] = useState({});

  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    contact_method: 'whatsapp',
    additional_notes: '',
    payment_method: 'pay_now'
  });

  // Fetch studios
  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const response = await axios.get(`${API}/studios`);
        setStudios(response.data);
      } catch (error) {
        console.error('Error fetching studios:', error);
      }
    };
    fetchStudios();
  }, []);

  const totalHours = selectedPack ? getPackHours(selectedPack) : 2;
  const maxSlots = Math.max(1, Math.ceil(totalHours / 2));

  // Calculate total price
  const serviceAddonsPrice = selectedAddons.reduce((sum, addonId) => {
    const addon = addons.find(a => a.id === addonId);
    return sum + (addon?.price || 0);
  }, 0);
  const totalPrice = (selectedPack?.price || 0) + serviceAddonsPrice;

  const stepLabels = ['Pack', 'Studio', 'Date', 'Options', 'Details'];

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setStudioConfig(null);
    setSelectedSlots([]);
    setMultiStudioSlots({ studio1: [], studio2: [] });
  };

  const handleStudioConfigChange = (config) => {
    setStudioConfig(config);
  };

  const handleSelectSlot = (slotKey) => {
    if (selectedSlots.includes(slotKey)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slotKey));
    } else if (selectedSlots.length < maxSlots) {
      setSelectedSlots([...selectedSlots, slotKey]);
    } else {
      toast.error(`You can only select ${maxSlots} slot(s) for this pack`);
    }
  };

  const handleMultiStudioSlotSelect = (studio, slotKey) => {
    const config = studioConfig?.multiStudio;
    if (!config) return;
    const max = studio === 'studio1'
      ? Math.ceil(config.studio1.hours / 2)
      : Math.ceil(config.studio2.hours / 2);
    const currentSlots = multiStudioSlots[studio];
    if (currentSlots.includes(slotKey)) {
      setMultiStudioSlots(prev => ({ ...prev, [studio]: currentSlots.filter(s => s !== slotKey) }));
    } else if (currentSlots.length < max) {
      setMultiStudioSlots(prev => ({ ...prev, [studio]: [...currentSlots, slotKey] }));
    } else {
      toast.error(`You can only select ${max} slot(s) for ${studio === 'studio1' ? 'Studio 1' : 'Studio 2'}`);
    }
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev =>
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  const isStudioSelected = () => {
    if (!studioConfig) return false;
    if (studioConfig.useMultiStudio) {
      return studioConfig.multiStudio?.studio1?.backgroundId && studioConfig.multiStudio?.studio2?.backgroundId;
    }
    return studioConfig.singleStudio?.backgroundId;
  };

  const areSlotsSelected = () => {
    if (studioConfig?.useMultiStudio) {
      return multiStudioSlots.studio1.length > 0 && multiStudioSlots.studio2.length > 0;
    }
    return selectedSlots.length > 0;
  };

  const nextStep = () => {
    if (step === 0 && !selectedPack) {
      toast.error('Please select a package');
      return;
    }
    if (step === 1 && !isStudioSelected()) {
      toast.error('Please select a studio and background');
      return;
    }
    if (step === 2 && !areSlotsSelected()) {
      toast.error('Please select at least one time slot');
      return;
    }
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    const { customer_name, customer_email, customer_phone } = customerInfo;
    if (!customer_name || !customer_email || !customer_phone) {
      setShowValidationErrors(true);
      toast.error('Please fill in all required fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customer_email)) {
      setShowValidationErrors(true);
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      // Build studio info
      let studioInfo = {};
      if (studioConfig) {
        if (studioConfig.useMultiStudio) {
          studioInfo = {
            multi_studio: true,
            studio1: studioConfig.multiStudio.studio1,
            studio2: studioConfig.multiStudio.studio2,
            studio1_slots: multiStudioSlots.studio1,
            studio2_slots: multiStudioSlots.studio2
          };
        } else {
          studioInfo = {
            multi_studio: false,
            studio_id: studioConfig.singleStudio.studioId,
            background_id: studioConfig.singleStudio.backgroundId
          };
        }
      }

      // Build time slots
      const timeSlots = studioConfig?.useMultiStudio
        ? [...multiStudioSlots.studio1, ...multiStudioSlots.studio2]
        : selectedSlots;

      const response = await axios.post(`${API}/service-inquiries`, {
        service_type: serviceType,
        full_name: customer_name,
        email: customer_email,
        phone: customer_phone,
        selected_pack: selectedPack?.id,
        preferred_date: timeSlots[0]?.split('_')[0] || '',
        preferred_time: timeSlots[0]?.split('_')[1] || '',
        time_slots: timeSlots,
        studio_config: studioInfo,
        addons: selectedAddons,
        production_options: options,
        custom_fields: customFieldValues,
        additional_notes: customerInfo.additional_notes,
        contact_method: customerInfo.contact_method,
        total_price: totalPrice,
        payment_method: customerInfo.payment_method
      });

      const inquiryId = response.data.id;

      // If pay_later, redirect to confirmation directly
      if (customerInfo.payment_method === 'pay_later') {
        navigate(`/services/confirmation?service=${serviceType}&id=${inquiryId}&status=pending`);
        return;
      }

      // Pay now - create Stripe checkout session
      const originUrl = window.location.origin;
      const checkoutResponse = await axios.post(`${API}/service-checkout/session`, {
        inquiry_id: inquiryId,
        origin_url: originUrl
      });

      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(error.response?.data?.detail || 'Error submitting your request. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="service-booking-page">
      <div className="py-24 md:py-32 px-6 md:px-12 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {/* Back Link */}
          <Link
            to={backLink}
            className="inline-flex items-center gap-2 text-foreground-muted hover:text-primary transition-colors text-sm mb-8"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {serviceTitle}
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium font-heading mb-6">
              Book Your <span className="text-primary">{serviceTitle}</span>
            </h1>
            {serviceSubtitle && (
              <p className="text-lg md:text-xl leading-relaxed tracking-wide font-light text-foreground-muted">
                {serviceSubtitle}
              </p>
            )}
          </motion.div>

          {/* Main Container - Glass Morphism */}
          <div
            className="p-8 md:p-12"
            style={{
              background: 'rgba(20, 20, 22, 0.7)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(234, 224, 213, 0.1)'
            }}
          >
            {/* Step Indicator */}
            <div className="flex justify-between mb-12">
              {stepLabels.map((label, index) => (
                <div key={index} className="flex items-center flex-1">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-body font-bold transition-colors duration-300 ${
                        step >= index ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                      data-testid={`step-indicator-${index}`}
                    >
                      {step > index ? <Check className="w-5 h-5" /> : index + 1}
                    </div>
                    <span className="text-xs text-foreground-muted hidden sm:block">{label}</span>
                  </div>
                  {index < stepLabels.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-colors duration-300 ${
                      step > index ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 0: Pack Selection */}
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-8 text-center">Choose Your Package</h2>
                <div className={`grid grid-cols-1 ${packs.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                  {packs.map((pack) => (
                    <div
                      key={pack.id}
                      onClick={() => handleSelectPack(pack)}
                      className={`cursor-pointer p-8 border-2 transition-all duration-300 relative ${
                        selectedPack?.id === pack.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      } ${pack.popular ? 'ring-2 ring-primary' : ''}`}
                      data-testid={`pack-${pack.id}`}
                    >
                      {pack.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
                          Popular
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-heading font-bold mb-2">{pack.name}</h3>
                        {pack.description && (
                          <p className="text-sm text-foreground-muted mb-4">{pack.description}</p>
                        )}
                        <div className="text-4xl font-heading font-bold text-primary mb-2">
                          {formatIDR(pack.price)}
                        </div>
                        {pack.duration && (
                          <p className="text-sm text-foreground-muted">{pack.duration}</p>
                        )}
                      </div>
                      {pack.features && (
                        <ul className="space-y-3">
                          {pack.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 1: Studio Selection */}
            {step === 1 && (
              <MultiStudioSelection
                studios={studios}
                totalHours={totalHours}
                studioConfig={studioConfig}
                onConfigChange={handleStudioConfigChange}
              />
            )}

            {/* Step 2: Date/Time Selection */}
            {step === 2 && (
              studioConfig?.useMultiStudio ? (
                <MultiStudioCalendar
                  studioConfig={studioConfig}
                  multiStudioSlots={multiStudioSlots}
                  onSelectSlot={handleMultiStudioSlotSelect}
                />
              ) : (
                <CalendlyCalendar
                  selectedSlots={selectedSlots}
                  onSelectSlot={handleSelectSlot}
                  maxSlots={maxSlots}
                />
              )
            )}

            {/* Step 3: Customize / Options */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Customize Your Session</h2>
                <p className="text-center text-sm text-foreground-muted mb-8">
                  Add editing services and personalize your experience
                </p>

                <div className="space-y-6">
                  {/* Production Options */}
                  <ExtraCameraSection
                    extraCameras={options.extra_cameras}
                    onChange={(value) => setOptions(prev => ({ ...prev, extra_cameras: value }))}
                    formatIDR={formatIDR}
                  />

                  <ExtraMicrophoneSection
                    extraMicrophones={options.extra_microphones}
                    onChange={(value) => setOptions(prev => ({ ...prev, extra_microphones: value }))}
                    formatIDR={formatIDR}
                  />

                  <VideoEditingSection
                    videoEditing={options.video_editing}
                    onChange={(value) => setOptions(prev => ({ ...prev, video_editing: value }))}
                    formatIDR={formatIDR}
                  />

                  <ReelsEditingSection
                    reels={options.reels}
                    onChange={(value) => setOptions(prev => ({ ...prev, reels: value }))}
                    formatIDR={formatIDR}
                  />

                  <FileDeliverySection
                    deliveryMethod={options.file_delivery}
                    hasEditingOptions={options.video_editing?.enabled || options.reels?.length > 0}
                    onChange={(value) => setOptions(prev => ({ ...prev, file_delivery: value }))}
                    formatIDR={formatIDR}
                  />

                  {/* Service-Specific Add-ons */}
                  {addons.length > 0 && (
                    <div className="pt-6 border-t border-border">
                      <h3 className="text-xl font-heading font-semibold mb-4">{serviceTitle} Options</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {addons.map((addon) => {
                          const isSelected = selectedAddons.includes(addon.id);
                          return (
                            <div
                              key={addon.id}
                              onClick={() => handleAddonToggle(addon.id)}
                              className={`cursor-pointer p-4 border-2 transition-all ${
                                isSelected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50'
                              }`}
                              data-testid={`addon-${addon.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`w-5 h-5 mt-0.5 border-2 flex items-center justify-center rounded ${
                                    isSelected ? 'border-primary bg-primary' : 'border-foreground-muted'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                                  </div>
                                  <div>
                                    <h4 className="font-medium">{addon.name}</h4>
                                    {addon.description && (
                                      <p className="text-sm text-foreground-muted">{addon.description}</p>
                                    )}
                                  </div>
                                </div>
                                {addon.price > 0 && (
                                  <span className="text-primary font-semibold text-sm whitespace-nowrap ml-2">
                                    +{formatIDR(addon.price)}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <div className="pt-6 border-t border-border">
                      <h3 className="text-xl font-heading font-semibold mb-4">Additional Information</h3>
                      <div className="space-y-4">
                        {customFields.map((field) => (
                          <div key={field.id}>
                            <label className="text-sm font-semibold text-foreground mb-3 block">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            {field.type === 'select' ? (
                              <select
                                value={customFieldValues[field.id] || ''}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200"
                                data-testid={`field-${field.id}`}
                              >
                                <option value="">Select...</option>
                                {field.options.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : field.type === 'textarea' ? (
                              <textarea
                                value={customFieldValues[field.id] || ''}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200 min-h-[100px]"
                                placeholder={field.placeholder}
                                data-testid={`field-${field.id}`}
                              />
                            ) : (
                              <input
                                type={field.type || 'text'}
                                value={customFieldValues[field.id] || ''}
                                onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200"
                                placeholder={field.placeholder}
                                data-testid={`field-${field.id}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Notes */}
                  <div className="pt-6 border-t border-border">
                    <label className="text-sm font-semibold text-foreground mb-3 block">Additional Notes (optional)</label>
                    <textarea
                      value={customerInfo.additional_notes}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, additional_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200 min-h-[100px]"
                      placeholder="Any specific requirements or questions..."
                      data-testid="additional-notes"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Customer Details */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Your Information</h2>
                <p className="text-center text-sm text-foreground-muted mb-8">
                  All fields are required <span className="text-red-500">*</span>
                </p>

                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Full Name <span className="text-red-500 text-lg">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerInfo.customer_name}
                      onChange={(e) => {
                        setCustomerInfo(prev => ({ ...prev, customer_name: e.target.value }));
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !customerInfo.customer_name ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                    {showValidationErrors && !customerInfo.customer_name && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Email <span className="text-red-500 text-lg">*</span>
                    </label>
                    <input
                      type="email"
                      value={customerInfo.customer_email}
                      onChange={(e) => {
                        setCustomerInfo(prev => ({ ...prev, customer_email: e.target.value }));
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !customerInfo.customer_email ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="john.doe@example.com"
                      data-testid="customer-email-input"
                    />
                    {showValidationErrors && !customerInfo.customer_email && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Contact Method <span className="text-red-500 text-lg">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      {['whatsapp', 'telegram'].map((method) => (
                        <button
                          key={method}
                          type="button"
                          onClick={() => setCustomerInfo(prev => ({ ...prev, contact_method: method }))}
                          className={`px-4 py-3 border-2 transition-all font-semibold ${
                            customerInfo.contact_method === method
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`contact-${method}-btn`}
                        >
                          {method === 'whatsapp' ? 'WhatsApp' : 'Telegram'}
                        </button>
                      ))}
                    </div>
                    <input
                      type="tel"
                      value={customerInfo.customer_phone}
                      onChange={(e) => {
                        setCustomerInfo(prev => ({ ...prev, customer_phone: e.target.value }));
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !customerInfo.customer_phone ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="+62 812 3456 7890"
                      data-testid="customer-phone-input"
                    />
                    {showValidationErrors && !customerInfo.customer_phone && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                  </div>

                  {/* Booking Summary */}
                  <div className="pt-6 border-t border-border">
                    <h3 className="font-heading font-semibold mb-4 text-lg">Your Booking Summary</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-muted">Service</span>
                        <span className="font-semibold">{serviceTitle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-muted">Package</span>
                        <span className="font-semibold">{selectedPack?.name}</span>
                      </div>
                      {studioConfig && (
                        <div className="flex justify-between">
                          <span className="text-foreground-muted">Studio</span>
                          <span className="font-semibold">
                            {studioConfig.useMultiStudio
                              ? `2 Studios (${studioConfig.multiStudio.studio1.hours}h + ${studioConfig.multiStudio.studio2.hours}h)`
                              : studios.find(s => s.id === studioConfig.singleStudio?.studioId)?.name || 'Selected'
                            }
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-foreground-muted">Time Slots</span>
                        <span className="font-semibold">
                          {studioConfig?.useMultiStudio
                            ? `${multiStudioSlots.studio1.length + multiStudioSlots.studio2.length} slot(s)`
                            : `${selectedSlots.length} slot(s)`
                          }
                        </span>
                      </div>
                      {selectedAddons.length > 0 && (
                        <div>
                          <span className="text-foreground-muted">Add-ons:</span>
                          <div className="mt-2 space-y-1">
                            {selectedAddons.map(addonId => {
                              const addon = addons.find(a => a.id === addonId);
                              return addon ? (
                                <div key={addonId} className="flex justify-between text-xs bg-muted px-3 py-2">
                                  <span>{addon.name}</span>
                                  {addon.price > 0 && <span className="text-primary">+{formatIDR(addon.price)}</span>}
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between pt-3 border-t border-border">
                        <span className="text-foreground-muted">Total</span>
                        <span className="text-xl font-heading font-bold text-primary" data-testid="summary-total-price">
                          {formatIDR(totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Options */}
                  <PaymentOptions
                    paymentMethod={customerInfo.payment_method}
                    onChange={(method) => setCustomerInfo(prev => ({ ...prev, payment_method: method }))}
                    formatIDR={formatIDR}
                    totalPrice={totalPrice}
                  />
                </div>
              </motion.div>
            )}

            {/* Navigation & Price */}
            <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                {step > 0 && (
                  <button
                    onClick={prevStep}
                    className="px-6 py-3 border border-border text-foreground hover:border-primary hover:text-primary transition-colors duration-200"
                    data-testid="prev-step-btn"
                  >
                    Previous
                  </button>
                )}
              </div>

              <div className="text-center">
                {selectedPack && (
                  <>
                    <p className="text-xs uppercase tracking-[0.2em] font-body font-bold text-primary mb-2">
                      {selectedPack.name}
                    </p>
                    <p className="text-3xl font-heading font-bold" data-testid="total-price">
                      {formatIDR(totalPrice)}
                    </p>
                  </>
                )}
              </div>

              <div>
                {step < 4 ? (
                  <button
                    onClick={nextStep}
                    className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200"
                    data-testid="next-step-btn"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200 disabled:opacity-50 flex items-center gap-2"
                    data-testid="submit-booking-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : customerInfo.payment_method === 'pay_now' ? (
                      'Pay Now with Stripe'
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
